import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Papa from 'papaparse';
import './Practice.css';
import { useManuscripts } from './ManuscriptContext';
import ImagePanel from './ImagePanel';
import PracticeTranscriptionPanel from './PracticeTranscriptionPanel';

const Practice = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { manuscripts } = useManuscripts();
    const [practiceData, setPracticeData] = useState([]);
    const [selectedExcerpt, setSelectedExcerpt] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [userInputs, setUserInputs] = useState([]);
    const [checkedResults, setCheckedResults] = useState([]);
    const [isChecked, setIsChecked] = useState(false);

    // Shared states for image panel
    const [zoom, setZoom] = useState(1);
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const [hoveredLine, setHoveredLine] = useState(null);
    const [showLineBoxes] = useState(true);
    const [showHoverText] = useState(false);

    // Function to parse the lines field from the CSV
    const parsePracticeLines = useCallback((input) => {
        if (!input || typeof input !== 'string') {
            console.error("Invalid input to parsePracticeLines:", input);
            return [];
        }


        // Handle a special case where multiple arrays are concatenated
        // This checks for a pattern like: "][" which indicates two arrays joined together
        if (input.includes('][')) {
            try {
                // Split by "][" to get individual arrays
                const arrayParts = input.split('][');
                const combinedArray = [];

                // Process each part
                for (let part of arrayParts) {
                    // Add brackets back if needed
                    if (!part.startsWith('[')) part = '[' + part;
                    if (!part.endsWith(']')) part = part + ']';

                    // Parse this part
                    const partArray = parsePracticeLines(part);
                    if (Array.isArray(partArray)) {
                        combinedArray.push(...partArray);
                    }
                }

                return combinedArray;
            } catch (e) {
                console.error("Failed to process concatenated arrays:", e);
                // Continue with regular parsing as fallback
            }
        }

        // Regular JSON array parsing
        if (input.startsWith('[') && input.endsWith(']')) {
            try {
                const parsed = JSON.parse(input);
                return parsed;
            } catch (e) {
                console.error("JSON parsing failed:", e);

                // If JSON parsing fails, try manual splitting
                try {
                    // This regex looks for comma-separated items within square brackets
                    const content = input.slice(1, -1); // Remove outer brackets

                    // Split by ", " which is the likely format in your array representation
                    const items = content.split('", "');

                    // Clean up the quotes around each item
                    const cleaned = items.map((item, index) => {
                        if (index === 0) {
                            return item.replace(/^"/, '');
                        } else if (index === items.length - 1) {
                            return item.replace(/"$/, '');
                        }
                        return item;
                    });

                    return cleaned;
                } catch (e2) {
                    console.error("Manual splitting also failed:", e2);
                    return [input]; // Return the input as a single line as a last resort
                }
            }
        }

        // Check if it might be a stringified array that's already been processed by CSV parser
        if (input.includes('","') || input.includes('", "')) {
            try {
                // This is a hack to handle a potential edge case
                const reconstructed = '[' + input + ']';
                return parsePracticeLines(reconstructed);
            } catch (e) {
                console.error("Reconstruction attempt failed:", e);
            }
        }

        // If it's not in array format, return as a single item
        return [input];
    }, []);

    const handleSelectExcerpt = useCallback((excerpt) => {

        // Ensure we have a valid excerpt
        if (!excerpt) {
            console.error("Invalid excerpt");
            setError("Selected excerpt is invalid");
            return;
        }

        // If lines array is empty but we have a text field, try to parse that
        let lines = Array.isArray(excerpt.lines) && excerpt.lines.length > 0
            ? [...excerpt.lines]
            : [];

        // Try to parse from text field if lines are empty
        if (lines.length === 0 && excerpt.text) {
            lines = parsePracticeLines(excerpt.text);
        }

        // Get the line count - prefer the actual lines length, then fall back to the numeric value
        let lineCount = lines.length;
        if (lineCount === 0) {
            // Try to get line count from CSV
            lineCount = parseInt(excerpt.lines, 10) || 0;

            // If we still don't have a line count, use a reasonable default
            if (lineCount === 0) {
                lineCount = 3; // Default to 3 lines if we have no better information
            }
        }


        // Pad or trim the array to match the line count
        if (lines.length < lineCount) {
            lines = [...lines, ...Array(lineCount - lines.length).fill('')];
        } else if (lines.length > lineCount && lineCount > 0) {
            lines = lines.slice(0, lineCount);
        }

        // Initialize the excerpt with the processed lines
        const processedExcerpt = {
            ...excerpt,
            lines: lines,
            lineCount: lineCount
        };

        setSelectedExcerpt(processedExcerpt);
        setUserInputs(Array(lineCount).fill(''));
        setCheckedResults([]);
        setIsChecked(false);

        // Update URL with selected excerpt ID
        if (id !== excerpt.id) {
            navigate(`/practice/${excerpt.id}`, { replace: true });
        }
    }, [parsePracticeLines, navigate, id]);

    // Function to normalize Arabic text by removing diacritics and normalizing letters
    const normalizeArabicText = useCallback((text) => {
        if (!text) return '';

        // Remove all diacritics (harakat, shadda, sukun, etc.)
        const removeDiacritics = text.replace(/[\u064B-\u065F\u0670]/g, '');

        // Normalize hamzas and other common variants
        return removeDiacritics
            .replace(/\u0623|\u0625|\u0622/g, '\u0627') // أ إ آ -> ا
            .replace(/\u0624/g, '\u0648') // ؤ -> و
            .replace(/\u0626/g, '\u064A') // ئ -> ي
            .replace(/\u0649/g, '\u064A') // ى -> ي (alif maqsura to ya)
            .replace(/\u0629/g, '\u0647'); // ة -> ه (ta marbuta to ha)
    }, []);

    // Tokenize text for comparison (split by spaces, keeping punctuation with words)
    const tokenizeText = useCallback((text) => {
        if (!text) return [];
        // Remove extra whitespace and split by spaces
        return text.trim().replace(/\s+/g, ' ').split(/\s+/);
    }, []);

    useEffect(() => {
        const loadPracticeData = async () => {
            try {
                const response = await fetch('/practice/practice.csv');
                const csvText = await response.text();

                Papa.parse(csvText, {
                    header: true,
                    skipEmptyLines: true,
                    complete: (results) => {

                        const processedData = results.data.map(row => {
                            try {
                                // Parse the lines using our specialized function
                                let linesArray = [];

                                const textSource = row.text;
                                if (typeof textSource === 'string') {
                                    linesArray = parsePracticeLines(textSource);
                                } else if (Array.isArray(textSource)) {
                                    linesArray = textSource;
                                }

                                // Make sure we have a valid array
                                if (!Array.isArray(linesArray)) {
                                    linesArray = [];
                                    console.error('Could not parse text into array for:', row.id);
                                }

                                // Get the line count directly from the CSV "lines" field or fallback to array length
                                // If 'lines' contains text when 'text' is missing, use parsed 'lines' as both count and content
                                const lineCount = parseInt(row.lines, 10) || linesArray.length || 0;

                                // Ensure we have the right number of lines in our array
                                if (linesArray.length < lineCount) {
                                    // Pad the array if the CSV specifies more lines than we have
                                    linesArray = [...linesArray, ...Array(lineCount - linesArray.length).fill('')];
                                } else if (linesArray.length > lineCount) {
                                    // Trim the array if we have more lines than specified
                                    linesArray = linesArray.slice(0, lineCount);
                                }


                                return {
                                    ...row,
                                    lines: linesArray,
                                    lineCount: lineCount
                                };
                            } catch (e) {
                                console.error('Error processing row:', row.id, e);
                                return {
                                    ...row,
                                    lines: [],
                                    lineCount: parseInt(row.lines, 10) || 0
                                };
                            }
                        });

                        setPracticeData(processedData);
                        setLoading(false);

                        // If id is provided, select that excerpt
                        if (id) {
                            const excerpt = processedData.find(item => item.id === id);
                            if (excerpt) {
                                handleSelectExcerpt(excerpt);
                            } else {
                                setError(`Excerpt with ID ${id} not found`);
                            }
                        }
                    },
                    error: (error) => {
                        console.error('Parse error:', error);
                        setError('Failed to parse practice data');
                        setLoading(false);
                    }
                });
            } catch (err) {
                console.error('Fetch error:', err);
                setError('Failed to load practice data');
                setLoading(false);
            }
        };

        loadPracticeData();
    }, [id, handleSelectExcerpt, parsePracticeLines]);

    const handleRandomExcerpt = () => {
        if (practiceData.length > 0) {
            const randomIndex = Math.floor(Math.random() * practiceData.length);
            handleSelectExcerpt(practiceData[randomIndex]);
        }
    };

    const handleInputChange = (index, value) => {
        const newInputs = [...userInputs];
        newInputs[index] = value;
        setUserInputs(newInputs);
    };

    const handleCheck = () => {
        if (!selectedExcerpt) return;

        const results = userInputs.map((input, index) => {
            // Get the correct line, or empty string if index is out of bounds
            const correctLine = index < selectedExcerpt.lines.length ? selectedExcerpt.lines[index] : '';

            // Normalize both texts for comparison
            const normalizedInput = normalizeArabicText(input);
            const normalizedCorrect = normalizeArabicText(correctLine);

            // Tokenize normalized texts for comparison
            const inputTokens = tokenizeText(normalizedInput);
            const correctTokens = tokenizeText(normalizedCorrect);

            // Tokenize original input and correct line
            const originalInputTokens = tokenizeText(input);
            const originalCorrectTokens = tokenizeText(correctLine);

            // Prepare the tokens with original and normalized versions
            const comparison = [];
            const maxLength = Math.max(inputTokens.length, correctTokens.length);

            for (let i = 0; i < maxLength; i++) {
                const userToken = i < inputTokens.length ? inputTokens[i] : '';
                const correctToken = i < correctTokens.length ? correctTokens[i] : '';
                const originalUserToken = i < originalInputTokens.length ? originalInputTokens[i] : '';
                const originalCorrectToken = i < originalCorrectTokens.length ? originalCorrectTokens[i] : '';
                const isCorrect = userToken === correctToken;

                comparison.push({
                    userToken: originalUserToken, // Original user token (unnormalized)
                    correctToken: originalCorrectToken, // Original correct token (with diacritics)
                    isCorrect
                });
            }

            return {
                userInput: input, // Keep original input
                correctText: correctLine, // Full original line
                comparison
            };
        });

        setCheckedResults(results);
        setIsChecked(true);
    };

    const resetPractice = () => {
        setCheckedResults([]);
        setIsChecked(false);
    };

    // Get manuscript metadata for the dropdown
    const getExcerptMetadata = useCallback((excerptId) => {
        if (!excerptId) return { title_lat: 'Unknown Manuscript' };

        // Extract the base manuscript ID by taking the part before the first hyphen
        // For example: "T002-1-1" -> "T002"
        const msId = excerptId.split('-')[0];


        const manuscript = manuscripts.find(ms => ms.manuscript_id === msId);

        if (!manuscript) {
            return { title_lat: 'Unknown Manuscript' };
        }

        return manuscript;
    }, [manuscripts]);

    if (loading) {
        return (
            <div className="container">
                <div className="main">
                    <div className="loading-container">
                        <div className="loading-spinner"></div>
                        <div className="loading-text">Loading practice data...</div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return <div className="error highlight">{error}</div>;
    }

    return (
        <div className="container">
            <div className="practice-container">
                <div className="practice-intro">
                    <p>
                        Here you can work on your Arabic palgeography skills by transcibing lines from manuscript excerpts. This is currently in a testing phase
                        so if you come across any issues or errors, please click "report a bug" (below)! NB: Diacritics (fatḥa, shadda, etc.) aren't counted and are hamzas
                         are normalized (e.g. و for ؤ, ا for أ, etc.). Finally, interlinear honorifics (e.g. رحمه الله) are skipped.
                    </p>
                </div>

                {!selectedExcerpt ? (
                    <div className="practice-selection">
                        <div className="selection-options">
                            <button
                                className="random-button"
                                onClick={handleRandomExcerpt}
                            >
                                Random Excerpt
                            </button>

                            <div className="dropdown-container">
                                <select
                                    className="excerpt-dropdown"
                                    onChange={(e) => {
                                        const selected = practiceData.find(item => item.id === e.target.value);
                                        if (selected) handleSelectExcerpt(selected);
                                    }}
                                    value={selectedExcerpt?.id || ''}
                                >
                                    <option value="">-- Select an excerpt --</option>
                                    {practiceData.map(item => {
                                        const msMetadata = getExcerptMetadata(item.id);
                                        return (
                                            <option key={item.id} value={item.id}>
                                                {msMetadata.title_lat} fol. {item.folio}
                                            </option>
                                        );
                                    })}
                                </select>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="practice-session">
                        <div className="session-header">
                            <h3>
                                {getExcerptMetadata(selectedExcerpt.id).title_lat} - Folio {selectedExcerpt.folio} (© {getExcerptMetadata(selectedExcerpt.id).copyright})
                            </h3>
                            <button
                                className="back-button"
                                onClick={() => {
                                    setSelectedExcerpt(null);
                                    navigate('/practice', { replace: true });
                                }}
                            >
                                ← Back to Selection
                            </button>
                        </div>

                        <div className="viewer-container">
                            <div className="panels-content">
                                <ImagePanel
                                    metadata={getExcerptMetadata(selectedExcerpt.id)}
                                    manuscriptId="practice"
                                    currentPage={selectedExcerpt.id}
                                    zoom={zoom}
                                    setZoom={setZoom}
                                    offset={offset}
                                    setOffset={setOffset}
                                    hoveredLine={hoveredLine}
                                    setHoveredLine={setHoveredLine}
                                    showLineBoxes={showLineBoxes}
                                    showHoverText={showHoverText}
                                    lines={[]} // We don't have line coordinates for practice
                                    fullWidth={false}
                                    practiceMode={true}
                                    customImagePath={`/practice/imgs/${selectedExcerpt.id}.jpeg`}
                                />

                                <PracticeTranscriptionPanel
                                    selectedExcerpt={selectedExcerpt}
                                    userInputs={userInputs}
                                    onInputChange={handleInputChange}
                                    checkedResults={checkedResults}
                                    isChecked={isChecked}
                                />
                            </div>

                            <div className="practice-controls">
                                {!isChecked ? (
                                    <button
                                        className="check-button"
                                        onClick={handleCheck}
                                    >
                                        Check Transcription
                                    </button>
                                ) : (
                                    <button
                                        className="try-again-button"
                                        onClick={resetPractice}
                                    >
                                        Try Again
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Practice;